import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

/**
 * The Monday (00:00) of the Monday–Sunday tracking week that contains `date`
 * (defaults to today). Falsy or unparseable input (e.g. an empty filter param)
 * also falls back to today rather than producing an "Invalid Date".
 */
export function mondayOf(date: dayjs.Dayjs | string | Date = dayjs()): dayjs.Dayjs {
  const parsed = dayjs(date);
  return (parsed.isValid() ? parsed : dayjs()).startOf('isoWeek');
}

/**
 * Checks if a date period consists of just a weekend
 * - It's 3 days + 1 hour or less (to account for DST transitions)
 * - It starts on Friday, Saturday, or Sunday
 * - It ends on Saturday, Sunday, or Monday
 *
 * @param startDate - The start date of the period
 * @param endDate - The end date of the period (can be null for open-ended periods)
 * @returns true if the period is just a weekend, false otherwise
 */
export function isWeekendPeriod(startDate: Date, endDate: Date | null): boolean {
  if (!endDate) {
    return false;
  }

  // Use 3 days + 1 hour grace period to account for DST transitions
  // DST changes typically add or subtract exactly 1 hour
  const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
  const oneHourInMs = 60 * 60 * 1000;
  const maxWeekendDuration = threeDaysInMs + oneHourInMs;

  if (endDate.getTime() - startDate.getTime() > maxWeekendDuration) {
    return false;
  }

  const weekendStartDays = [0, 5, 6]; // Sunday, Friday, Saturday
  const weekendEndDays = [0, 1, 6]; // Sunday, Monday, Saturday

  return weekendStartDays.includes(startDate.getDay()) && weekendEndDays.includes(endDate.getDay());
}
